import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { filterValuesSeparator } from 'shared-helpers/consts';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { getCompetencyMatrixColumnsByView } from 'lib/competencies';
import { ApiDownloadButton, EmptyIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { FiltersProvider } from 'components/common/filter';
import { StatCount } from 'components/common/stats';
import { UsersPagination } from 'components/users';
import { CompetencyMatrixSubHeader, CompetencyMatrixUsersList } from '.';

import type {
  GetCompetenciesRes,
  GetCompetencyProfilesRes,
  GetUsersForCompetencyMatrixRes
} from 'shared-types/responses';
import type { CompetencyInMatrix } from 'lib/competencies/getCompetencyMatrixColumnsByView';

export type CompetencyMatrixView =
  | 'competencies'
  | 'competency-levels'
  | 'profiles';

const CompetencyMatrix = () => {
  const { language, messages, user } = useReduxState();

  const router = useRouter();

  const [columns, setColumns] = useState<CompetencyInMatrix[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);

  const { data: competencies, loading: competenciesLoading } =
    useApiData<GetCompetenciesRes>({ url: `/admin/competencies?limit=-1` });

  const { data: profiles, loading: profilesLoading } =
    useApiData<GetCompetencyProfilesRes>({
      url: '/admin/competency-profiles?limit=-1'
    });

  const {
    data,
    loading: usersLoading,
    setLoading: setUsersLoading
  } = useApiData<GetUsersForCompetencyMatrixRes>({
    url:
      `/admin/users-competency-matrix` +
      `?excludeGuestUsers=true` +
      `&excludeAnonymousUsers=true` +
      Object.keys(router?.query || {})
        .map((key) => `&${key}=${router.query[key]}`)
        .join('') +
      (router.query?.sortBy === 'competencies'
        ? `&columns=${columns
            .map(
              (column) =>
                `${column.value}${column.level ? `.${column.level}` : ''}`
            )
            .join(filterValuesSeparator)}`
        : '')
  });

  useEffect(() => {
    setColumnsLoading(true);
    setColumns(
      getCompetencyMatrixColumnsByView({
        competencies,
        language,
        profiles,
        query: router.query
      })
    );
    setColumnsLoading(false);
  }, [
    competencies,
    profiles,
    router.query?.view,
    router.query?.competencesInColumns,
    router.query?.profilesInColumns
  ]);

  return (
    <FiltersProvider
      filters={{
        achievements: true,
        ambitions: true,
        calendarEvents: true,
        competenceProfiles: true,
        competences: true,
        courses: true,
        groups: true,
        paths: true,
        supervisors:
          user?.roles.includes('superadmin') ||
          user?.roles.includes('supervisor'),
        userAttributes: true
      }}
      onBeforeSubmitOrClear={() => setUsersLoading(true)}
    >
      <Box
        headerContent={
          <ApiDownloadButton
            category="success"
            disabled={!columns?.length || !data?.users?.length}
            filename={`${messages.competencyMatrix}.xlsx`}
            icon="file-excel"
            size="header"
            text={messages.commonExportToExcel}
            url={
              `/admin/users-competency-matrix/excel-export` +
              `?excludeGuestUsers=true` +
              `&excludeAnonymousUsers=true` +
              `&columns=${columns
                .map(
                  (column) =>
                    `${column.value}${column.level ? `.${column.level}` : ''}`
                )
                .join(filterValuesSeparator)}` +
              Object.keys(router?.query || {})
                .map((key) => `&${key}=${router.query[key]}`)
                .join('')
            }
          />
        }
        headerContentWrap={true}
        loading={
          competenciesLoading ||
          profilesLoading ||
          usersLoading ||
          columnsLoading
        }
        padding={0}
        subHeaderContent={
          <CompetencyMatrixSubHeader
            setColumnsLoading={setColumnsLoading}
            setUsersLoading={setUsersLoading}
            view={router.query?.view as CompetencyMatrixView | undefined}
          />
        }
        title={
          <div style={{ display: 'flex', gap: 10 }}>
            <div>{messages.competencyMatrix}</div>
            <StatCount
              isLarge={true}
              label={getMessageWithPlaceholders({
                message: messages.usersNumberOfUsers,
                placeholders: { numberOfUsers: '' }
              })}
              value={data?.usersCount || 0}
            />
          </div>
        }
      >
        {columns?.length && data?.users?.length ? (
          <CompetencyMatrixUsersList
            columns={columns}
            users={data.users}
            view={router.query?.view as CompetencyMatrixView | undefined}
          />
        ) : (
          <EmptyIndicator
            icon="table"
            title={
              !data?.usersCount
                ? messages.commonNoUsers
                : messages.commonNoCompetencies
            }
          />
        )}
      </Box>

      {data?.pages > 0 && <UsersPagination pages={data?.pages || 0} />}
    </FiltersProvider>
  );
};

export default CompetencyMatrix;
